import { lazy, type ReactElement, Suspense } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import { AppShell } from '@/app/layouts/AppShell';
import { ProtectedRoute } from '@/app/router/ProtectedRoute';
import { ScopeGuard } from '@/app/router/ScopeGuard';
import { Skeleton } from '@/shared/ui/Skeleton';

const DashboardPage = lazy(() =>
  import('@/pages/dashboard').then((m) => ({ default: m.DashboardPage })),
);
const LoginPage = lazy(() =>
  import('@/pages/login').then((m) => ({ default: m.LoginPage })),
);
const ProfilePage = lazy(() =>
  import('@/pages/profile').then((m) => ({ default: m.ProfilePage })),
);
const ChangePasswordPage = lazy(() =>
  import('@/pages/change-password').then((m) => ({ default: m.ChangePasswordPage })),
);
const BranchesListPage = lazy(() =>
  import('@/pages/branches').then((m) => ({ default: m.BranchesListPage })),
);
const BranchFormPage = lazy(() =>
  import('@/pages/branches').then((m) => ({ default: m.BranchFormPage })),
);
const UsersListPage = lazy(() =>
  import('@/pages/users').then((m) => ({ default: m.UsersListPage })),
);
const UserFormPage = lazy(() =>
  import('@/pages/users').then((m) => ({ default: m.UserFormPage })),
);
const MunicipalitiesListPage = lazy(() =>
  import('@/pages/municipalities').then((m) => ({
    default: m.MunicipalitiesListPage,
  })),
);
const MunicipalityFormPage = lazy(() =>
  import('@/pages/municipalities').then((m) => ({
    default: m.MunicipalityFormPage,
  })),
);
const SpecialtiesListPage = lazy(() =>
  import('@/pages/specialties').then((m) => ({ default: m.SpecialtiesListPage })),
);
const SpecialtyFormPage = lazy(() =>
  import('@/pages/specialties').then((m) => ({ default: m.SpecialtyFormPage })),
);
const TeamsListPage = lazy(() =>
  import('@/pages/teams').then((m) => ({ default: m.TeamsListPage })),
);
const TeamFormPage = lazy(() =>
  import('@/pages/teams').then((m) => ({ default: m.TeamFormPage })),
);
const StandardsListPage = lazy(() =>
  import('@/pages/standards').then((m) => ({ default: m.StandardsListPage })),
);
const StandardFormPage = lazy(() =>
  import('@/pages/standards').then((m) => ({ default: m.StandardFormPage })),
);
const AgentTypesListPage = lazy(() =>
  import('@/pages/agent-types').then((m) => ({ default: m.AgentTypesListPage })),
);
const AgentTypeFormPage = lazy(() =>
  import('@/pages/agent-types').then((m) => ({ default: m.AgentTypeFormPage })),
);
const DoctorsListPage = lazy(() =>
  import('@/pages/doctors').then((m) => ({ default: m.DoctorsListPage })),
);
const DoctorFormPage = lazy(() =>
  import('@/pages/doctors').then((m) => ({ default: m.DoctorFormPage })),
);
const AgentsListPage = lazy(() =>
  import('@/pages/agents').then((m) => ({ default: m.AgentsListPage })),
);
const AgentFormPage = lazy(() =>
  import('@/pages/agents').then((m) => ({ default: m.AgentFormPage })),
);
const AffiliatesListPage = lazy(() =>
  import('@/pages/affiliates').then((m) => ({ default: m.AffiliatesListPage })),
);
const AffiliateFormPage = lazy(() =>
  import('@/pages/affiliates').then((m) => ({ default: m.AffiliateFormPage })),
);
const SuppliersListPage = lazy(() =>
  import('@/pages/suppliers').then((m) => ({ default: m.SuppliersListPage })),
);
const SupplierFormPage = lazy(() =>
  import('@/pages/suppliers').then((m) => ({ default: m.SupplierFormPage })),
);
const TariffsListPage = lazy(() =>
  import('@/pages/tariffs').then((m) => ({ default: m.TariffsListPage })),
);
const TariffFormPage = lazy(() =>
  import('@/pages/tariffs').then((m) => ({ default: m.TariffFormPage })),
);
const ContractsListPage = lazy(() =>
  import('@/pages/contracts').then((m) => ({ default: m.ContractsListPage })),
);
const ContractFormPage = lazy(() =>
  import('@/pages/contracts').then((m) => ({ default: m.ContractFormPage })),
);
const AppointmentsListPage = lazy(() =>
  import('@/pages/appointments').then((m) => ({
    default: m.AppointmentsListPage,
  })),
);
const AppointmentSchedulePage = lazy(() =>
  import('@/pages/appointments').then((m) => ({
    default: m.AppointmentSchedulePage,
  })),
);
const AppointmentDetailPage = lazy(() =>
  import('@/pages/appointments').then((m) => ({
    default: m.AppointmentDetailPage,
  })),
);
const PendingAppointmentsPage = lazy(() =>
  import('@/pages/appointments').then((m) => ({
    default: m.PendingAppointmentsPage,
  })),
);
const ReceiptsPage = lazy(() =>
  import('@/pages/receipts').then((m) => ({ default: m.ReceiptsPage })),
);
const ProvisionalsPage = lazy(() =>
  import('@/pages/provisionals').then((m) => ({
    default: m.ProvisionalsPage,
  })),
);
const CardsPage = lazy(() =>
  import('@/pages/cards').then((m) => ({ default: m.CardsPage })),
);
const StationeryPage = lazy(() =>
  import('@/pages/stationery').then((m) => ({ default: m.StationeryPage })),
);
const BonificationsPage = lazy(() =>
  import('@/pages/bonifications').then((m) => ({
    default: m.BonificationsPage,
  })),
);
const DiscountsPage = lazy(() =>
  import('@/pages/discounts').then((m) => ({ default: m.DiscountsPage })),
);
const CollectionFormsPage = lazy(() =>
  import('@/pages/collection-forms').then((m) => ({
    default: m.CollectionFormsPage,
  })),
);
const ChecksPage = lazy(() =>
  import('@/pages/checks').then((m) => ({ default: m.ChecksPage })),
);
const AdvertisingMaterialsPage = lazy(() =>
  import('@/pages/advertising-materials').then((m) => ({
    default: m.AdvertisingMaterialsPage,
  })),
);
const InvoicesListPage = lazy(() =>
  import('@/pages/invoices').then((m) => ({ default: m.InvoicesListPage })),
);
const InvoiceFormPage = lazy(() =>
  import('@/pages/invoices').then((m) => ({ default: m.InvoiceFormPage })),
);
const InstallmentsListPage = lazy(() =>
  import('@/pages/installments').then((m) => ({
    default: m.InstallmentsListPage,
  })),
);
const CollectionsPage = lazy(() =>
  import('@/pages/collections').then((m) => ({ default: m.CollectionsPage })),
);
const InventoryPage = lazy(() =>
  import('@/pages/inventory').then((m) => ({ default: m.InventoryPage })),
);
const ItemTypesListPage = lazy(() =>
  import('@/pages/inventory').then((m) => ({ default: m.ItemTypesListPage })),
);
const ItemTypeFormPage = lazy(() =>
  import('@/pages/inventory').then((m) => ({ default: m.ItemTypeFormPage })),
);
const PurchasesListPage = lazy(() =>
  import('@/pages/purchases').then((m) => ({ default: m.PurchasesListPage })),
);
const PurchaseFormPage = lazy(() =>
  import('@/pages/purchases').then((m) => ({ default: m.PurchaseFormPage })),
);
const WarehousePage = lazy(() =>
  import('@/pages/warehouse').then((m) => ({ default: m.WarehousePage })),
);
const FixedAssetsListPage = lazy(() =>
  import('@/pages/fixed-assets').then((m) => ({
    default: m.FixedAssetsListPage,
  })),
);
const FixedAssetFormPage = lazy(() =>
  import('@/pages/fixed-assets').then((m) => ({
    default: m.FixedAssetFormPage,
  })),
);
const ReportsCenterPage = lazy(() =>
  import('@/pages/reports').then((m) => ({ default: m.ReportsCenterPage })),
);
const SiigoInterfacesCenterPage = lazy(() =>
  import('@/pages/siigo-interfaces').then((m) => ({
    default: m.SiigoInterfacesCenterPage,
  })),
);
const NotFoundPage = lazy(() =>
  import('@/pages/not-found').then((m) => ({ default: m.NotFoundPage })),
);

function RouteFallback(): ReactElement {
  return (
    <div className="mx-auto max-w-7xl space-y-6 animate-fade-in">
      <Skeleton className="h-12 w-1/3" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-32" />
        ))}
      </div>
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

const lazyRoute = (Component: React.LazyExoticComponent<() => ReactElement>): ReactElement => (
  <Suspense fallback={<RouteFallback />}>
    <Component />
  </Suspense>
);

const router = createBrowserRouter([
  {
    path: '/login',
    element: lazyRoute(LoginPage),
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppShell />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: lazyRoute(DashboardPage) },
      { path: 'perfil', element: lazyRoute(ProfilePage) },
      { path: 'cambiar-clave', element: lazyRoute(ChangePasswordPage) },
      {
        path: 'admin/sucursales',
        element: (
          <ScopeGuard required={['branches:read']}>
            {lazyRoute(BranchesListPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'admin/sucursales/nueva',
        element: (
          <ScopeGuard required={['branches:write']}>
            {lazyRoute(BranchFormPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'admin/sucursales/:id',
        element: (
          <ScopeGuard required={['branches:update']}>
            {lazyRoute(BranchFormPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'admin/usuarios',
        element: (
          <ScopeGuard required={['users:read']}>
            {lazyRoute(UsersListPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'admin/usuarios/nuevo',
        element: (
          <ScopeGuard required={['users:write']}>
            {lazyRoute(UserFormPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'admin/usuarios/:id',
        element: (
          <ScopeGuard required={['users:update']}>
            {lazyRoute(UserFormPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'admin/municipios',
        element: (
          <ScopeGuard required={['municipalities:read']}>
            {lazyRoute(MunicipalitiesListPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'admin/municipios/nuevo',
        element: (
          <ScopeGuard required={['municipalities:write']}>
            {lazyRoute(MunicipalityFormPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'admin/municipios/:id',
        element: (
          <ScopeGuard required={['municipalities:update']}>
            {lazyRoute(MunicipalityFormPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'admin/especialidades',
        element: (
          <ScopeGuard required={['specialties:read']}>
            {lazyRoute(SpecialtiesListPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'admin/especialidades/nueva',
        element: (
          <ScopeGuard required={['specialties:write']}>
            {lazyRoute(SpecialtyFormPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'admin/especialidades/:id',
        element: (
          <ScopeGuard required={['specialties:update']}>
            {lazyRoute(SpecialtyFormPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'admin/equipos',
        element: (
          <ScopeGuard required={['teams:read']}>
            {lazyRoute(TeamsListPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'admin/equipos/nuevo',
        element: (
          <ScopeGuard required={['teams:write']}>
            {lazyRoute(TeamFormPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'admin/equipos/:id',
        element: (
          <ScopeGuard required={['teams:update']}>
            {lazyRoute(TeamFormPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'admin/estandares',
        element: (
          <ScopeGuard required={['standards:read']}>
            {lazyRoute(StandardsListPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'admin/estandares/nuevo',
        element: (
          <ScopeGuard required={['standards:write']}>
            {lazyRoute(StandardFormPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'admin/estandares/:id',
        element: (
          <ScopeGuard required={['standards:update']}>
            {lazyRoute(StandardFormPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'admin/tipos-agente',
        element: (
          <ScopeGuard required={['agent-types:read']}>
            {lazyRoute(AgentTypesListPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'admin/tipos-agente/nuevo',
        element: (
          <ScopeGuard required={['agent-types:write']}>
            {lazyRoute(AgentTypeFormPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'admin/tipos-agente/:id',
        element: (
          <ScopeGuard required={['agent-types:update']}>
            {lazyRoute(AgentTypeFormPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'admin/doctores',
        element: (
          <ScopeGuard required={['doctors:read']}>
            {lazyRoute(DoctorsListPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'admin/doctores/nuevo',
        element: (
          <ScopeGuard required={['doctors:write']}>
            {lazyRoute(DoctorFormPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'admin/doctores/:id',
        element: (
          <ScopeGuard required={['doctors:update']}>
            {lazyRoute(DoctorFormPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'admin/agentes',
        element: (
          <ScopeGuard required={['agents:read']}>
            {lazyRoute(AgentsListPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'admin/agentes/nuevo',
        element: (
          <ScopeGuard required={['agents:write']}>
            {lazyRoute(AgentFormPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'admin/agentes/:id',
        element: (
          <ScopeGuard required={['agents:update']}>
            {lazyRoute(AgentFormPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'admin/afiliados',
        element: (
          <ScopeGuard required={['affiliates:read']}>
            {lazyRoute(AffiliatesListPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'admin/afiliados/nuevo',
        element: (
          <ScopeGuard required={['affiliates:write']}>
            {lazyRoute(AffiliateFormPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'admin/afiliados/:idNumber',
        element: (
          <ScopeGuard required={['affiliates:update']}>
            {lazyRoute(AffiliateFormPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'admin/proveedores',
        element: (
          <ScopeGuard required={['suppliers:read']}>
            {lazyRoute(SuppliersListPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'admin/proveedores/nuevo',
        element: (
          <ScopeGuard required={['suppliers:write']}>
            {lazyRoute(SupplierFormPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'admin/proveedores/:id',
        element: (
          <ScopeGuard required={['suppliers:update']}>
            {lazyRoute(SupplierFormPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'admin/tarifas',
        element: (
          <ScopeGuard required={['tariffs:read']}>
            {lazyRoute(TariffsListPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'admin/tarifas/nueva',
        element: (
          <ScopeGuard required={['tariffs:write']}>
            {lazyRoute(TariffFormPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'admin/tarifas/:id',
        element: (
          <ScopeGuard required={['tariffs:update']}>
            {lazyRoute(TariffFormPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'contratos',
        element: (
          <ScopeGuard required={['contracts:read']}>
            {lazyRoute(ContractsListPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'contratos/nuevo',
        element: (
          <ScopeGuard required={['contracts:write']}>
            {lazyRoute(ContractFormPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'contratos/:id',
        element: (
          <ScopeGuard required={['contracts:read']}>
            {lazyRoute(ContractFormPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'citas',
        element: (
          <ScopeGuard required={['appointments:read']}>
            {lazyRoute(AppointmentsListPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'citas/pendientes',
        element: (
          <ScopeGuard required={['appointments:read']}>
            {lazyRoute(PendingAppointmentsPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'citas/nueva',
        element: (
          <ScopeGuard required={['appointments:write']}>
            {lazyRoute(AppointmentSchedulePage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'citas/:id',
        element: (
          <ScopeGuard required={['appointments:read']}>
            {lazyRoute(AppointmentDetailPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'recibos',
        element: (
          <ScopeGuard required={['receipts:read']}>
            {lazyRoute(ReceiptsPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'provisionales',
        element: (
          <ScopeGuard required={['provisionals:read']}>
            {lazyRoute(ProvisionalsPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'tarjetas-cobro',
        element: (
          <ScopeGuard required={['cards:read']}>
            {lazyRoute(CardsPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'papeleria',
        element: (
          <ScopeGuard required={['stationery:read']}>
            {lazyRoute(StationeryPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'bonificaciones',
        element: (
          <ScopeGuard required={['bonifications:read']}>
            {lazyRoute(BonificationsPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'descuentos',
        element: (
          <ScopeGuard required={['discounts:read']}>
            {lazyRoute(DiscountsPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'planillas',
        element: (
          <ScopeGuard required={['collection-forms:read']}>
            {lazyRoute(CollectionFormsPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'cheques',
        element: (
          <ScopeGuard required={['checks:read']}>
            {lazyRoute(ChecksPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'publicidad',
        element: (
          <ScopeGuard required={['advertising-materials:read']}>
            {lazyRoute(AdvertisingMaterialsPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'cuotas',
        element: (
          <ScopeGuard required={['installments:read']}>
            {lazyRoute(InstallmentsListPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'facturas',
        element: (
          <ScopeGuard required={['invoices:read']}>
            {lazyRoute(InvoicesListPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'facturas/nueva',
        element: (
          <ScopeGuard required={['invoices:write']}>
            {lazyRoute(InvoiceFormPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'facturas/:id',
        element: (
          <ScopeGuard required={['invoices:read']}>
            {lazyRoute(InvoiceFormPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'recaudos',
        element: (
          <ScopeGuard required={['collections:read']}>
            {lazyRoute(CollectionsPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'inventario',
        element: (
          <ScopeGuard required={['inventory:read']}>
            {lazyRoute(InventoryPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'inventario/tipos',
        element: (
          <ScopeGuard required={['inventory:read']}>
            {lazyRoute(ItemTypesListPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'inventario/tipos/nuevo',
        element: (
          <ScopeGuard required={['inventory:write']}>
            {lazyRoute(ItemTypeFormPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'inventario/tipos/:id',
        element: (
          <ScopeGuard required={['inventory:update']}>
            {lazyRoute(ItemTypeFormPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'compras',
        element: (
          <ScopeGuard required={['purchases:read']}>
            {lazyRoute(PurchasesListPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'compras/nueva',
        element: (
          <ScopeGuard required={['purchases:write']}>
            {lazyRoute(PurchaseFormPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'compras/:id',
        element: (
          <ScopeGuard required={['purchases:read']}>
            {lazyRoute(PurchaseFormPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'bodega',
        element: (
          <ScopeGuard required={['warehouse:read']}>
            {lazyRoute(WarehousePage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'activos',
        element: (
          <ScopeGuard required={['fixed-assets:read']}>
            {lazyRoute(FixedAssetsListPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'activos/nuevo',
        element: (
          <ScopeGuard required={['fixed-assets:write']}>
            {lazyRoute(FixedAssetFormPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'activos/:id',
        element: (
          <ScopeGuard required={['fixed-assets:read']}>
            {lazyRoute(FixedAssetFormPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'reportes',
        element: (
          <ScopeGuard required={['reports:read']}>
            {lazyRoute(ReportsCenterPage)}
          </ScopeGuard>
        ),
      },
      {
        path: 'siigo',
        element: lazyRoute(SiigoInterfacesCenterPage),
      },
      { path: '*', element: lazyRoute(NotFoundPage) },
    ],
  },
]);

export function AppRouter(): ReactElement {
  return <RouterProvider router={router} />;
}
